import { useState } from "react";
import { api } from "../api";
import type { Customer, ServerMeta, WorkspaceSnapshot } from "../types";
import { fmtTs } from "../utils";
import { CustomerForm } from "./CustomerForm";
import { ContactsCard } from "./ContactsCard";
import { AccountsPanel } from "./AccountsPanel";
import { SignDialog } from "./SignDialog";
import { LicensesCard } from "./LicensesCard";
import { LiveCountdown } from "./LiveCountdown";
import { ConversationsPanel } from "./ConversationsPanel";
import { PromptsPanel } from "./PromptsPanel";

type Tab = "accounts" | "conversations" | "prompts" | "licenses";

export function CustomerDetail({
  meta,
  data,
  onChanged,
  onDeleted,
  flash,
}: {
  meta: ServerMeta | null;
  data: { customer: Customer; workspace: WorkspaceSnapshot };
  onChanged: () => Promise<void>;
  onDeleted: () => Promise<void>;
  flash: (kind: "info" | "error", msg: string) => void;
}) {
  const { customer, workspace } = data;
  const [tab, setTab] = useState<Tab>("accounts");
  const [showEdit, setShowEdit] = useState(false);
  const [showSign, setShowSign] = useState(false);

  const latest = customer.licenses?.[customer.licenses.length - 1];

  const onUpdate = async (body: Partial<Customer>) => {
    try {
      await api.updateCustomer(customer.id, body);
      setShowEdit(false);
      await onChanged();
      flash("info", "已更新");
    } catch (e: unknown) {
      flash("error", (e as Error).message);
    }
  };

  const onDelete = async () => {
    if (!confirm(`真的要删除客户「${customer.name}」吗？工作区文件不会被删，主台账记录会消失。`)) return;
    try {
      await api.deleteCustomer(customer.id);
      await onDeleted();
    } catch (e: unknown) {
      flash("error", (e as Error).message);
    }
  };

  return (
    <div className="space-y-4 px-6 py-4">
      <section className="card overflow-hidden">
        <header className="flex items-start justify-between gap-4 px-5 py-4">
          <div>
            <div className="flex items-baseline gap-3">
              <h2 className="text-xl font-semibold">{customer.name}</h2>
              <span className="font-mono text-xs text-ink-500">{customer.id}</span>
            </div>
            <div className="mt-1 text-xs text-ink-500">
              更新 {fmtTs(customer.updated_at)} · 创建 {fmtTs(customer.created_at)}
            </div>
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary" onClick={() => setShowEdit(true)}>
              编辑
            </button>
            <button
              className="btn-primary"
              onClick={() => setShowSign(true)}
              disabled={!meta?.has_private_key}
              title={meta?.has_private_key ? "签发新 license" : "私钥缺失，无法签发"}
            >
              签发 license
            </button>
            <button className="btn-danger" onClick={onDelete}>
              删除
            </button>
          </div>
        </header>

        <div className="grid grid-cols-2 gap-x-6 gap-y-2 border-t border-ink-100 px-5 py-3 text-sm md:grid-cols-4">
          <Field label="渠道">{customer.channel || "-"}</Field>
          <Field label="成交金额">
            {customer.price_cny != null ? `¥${customer.price_cny}` : "-"}
          </Field>
          <Field label="付款时间">{customer.paid_at || "-"}</Field>
          <Field label="指纹码">
            <span className="font-mono text-xs" title={customer.fingerprint}>
              {customer.fingerprint || "-"}
            </span>
          </Field>
          <Field label="当前 license" className="col-span-2">
            {latest ? <LicenseSummary lic={latest} /> : <span className="text-ink-400">未签发</span>}
          </Field>
          <Field label="工作区目录" className="col-span-2">
            <code className="font-mono text-xs text-ink-600">{workspace.paths.root}</code>
          </Field>
          {customer.note && (
            <Field label="备注" className="col-span-4">
              <p className="whitespace-pre-wrap text-ink-700">{customer.note}</p>
            </Field>
          )}
        </div>
      </section>

      <ContactsCard
        customerId={customer.id}
        contacts={customer.contacts ?? []}
        contactKinds={meta?.contact_kinds ?? []}
        onChanged={onChanged}
        flash={flash}
      />

      <nav className="flex gap-1 border-b border-ink-200">
        <TabButton active={tab === "accounts"} onClick={() => setTab("accounts")}>
          运营账号 <Badge n={customer.accounts?.length ?? 0} />
        </TabButton>
        <TabButton active={tab === "conversations"} onClick={() => setTab("conversations")}>
          交流记录 <Badge n={workspace.conversations.length} />
        </TabButton>
        <TabButton active={tab === "prompts"} onClick={() => setTab("prompts")}>
          Prompt 模板 <Badge n={workspace.prompts.length} />
        </TabButton>
        <TabButton active={tab === "licenses"} onClick={() => setTab("licenses")}>
          License 历史 <Badge n={customer.licenses?.length ?? 0} />
        </TabButton>
      </nav>

      <div>
        {tab === "accounts" && (
          <AccountsPanel
            customerId={customer.id}
            accounts={customer.accounts ?? []}
            onChanged={onChanged}
            flash={flash}
          />
        )}
        {tab === "conversations" && (
          <ConversationsPanel
            customerId={customer.id}
            entries={workspace.conversations}
            directions={meta?.conversation_directions ?? []}
            channels={meta?.conversation_channels ?? []}
            onChanged={onChanged}
            flash={flash}
          />
        )}
        {tab === "prompts" && (
          <PromptsPanel
            customerId={customer.id}
            items={workspace.prompts}
            onChanged={onChanged}
            flash={flash}
          />
        )}
        {tab === "licenses" && (
          <LicensesCard customerId={customer.id} licenses={customer.licenses ?? []} />
        )}
      </div>

      {showEdit && (
        <CustomerForm
          title={`编辑客户：${customer.name}`}
          initial={customer}
          contactKinds={meta?.contact_kinds ?? []}
          onClose={() => setShowEdit(false)}
          onSubmit={onUpdate}
        />
      )}
      {showSign && (
        <SignDialog
          customer={customer}
          onClose={() => setShowSign(false)}
          onSigned={async () => {
            setShowSign(false);
            setTab("licenses");
            await onChanged();
            flash("info", "已签发 license，已自动切到 License 历史");
          }}
          flash={flash}
        />
      )}
    </div>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <div className="text-xs text-ink-500">{label}</div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      className={
        active
          ? "border-b-2 border-indigo-600 px-4 py-2 text-sm font-medium text-indigo-700"
          : "border-b-2 border-transparent px-4 py-2 text-sm text-ink-600 hover:text-ink-900"
      }
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function Badge({ n }: { n: number }) {
  if (!n) return null;
  return (
    <span className="ml-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-ink-100 px-1.5 text-xs text-ink-700">
      {n}
    </span>
  );
}

function LicenseSummary({ lic }: { lic: Customer["licenses"][number] }) {
  return (
    <div className="space-y-0.5">
      <div className="font-mono text-xs">serial {lic.serial}</div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span>
          {fmtTs(lic.not_before)} → {fmtTs(lic.not_after)}
        </span>
        <LiveCountdown notAfter={lic.not_after} />
        <span className="chip">频道上限 {lic.max_templates}</span>
      </div>
    </div>
  );
}
